import {db, LocalStatus, } from '@src/db.ts';
import {
    Employee,WorkdayWithEmployee,
    WorkEventCreateInputRequest,
    WorkEventOutput
} from "@src/httpClient/__generated__/Api.ts";
import {apiClient} from "@src/httpClient/api.ts";
import {addEventOffline, syncEmployee, syncWorkdays} from "@src/offline/offline.ts";
import {create} from 'zustand'


export const syncEventsOutbox = async () => {
    const batch = await db.outbox.orderBy('createdAt').toArray();
    if (batch?.length === 0) return;

    const events = batch.map(e => e.payload as WorkEventCreateInputRequest);

    try {
        const response = await apiClient.api.eventsIngest(events);
        for (const item of response.data.created) {
            const local = await db.events.where({idempotency_key: item.idempotency_key}).first();
            const outboxElem = batch.find(e => e.payload.idempotency_key === item.idempotency_key);
            if (local) {
                await db.events.update(local.id!, {id: item.id, localStatus: LocalStatus.synced});
            }

            if (!outboxElem) continue
            await db.outbox.delete(outboxElem.id!);
        }
    } catch (e: any) {
        for (const item of batch) {
            const tryCount = (item.tryCount ?? 0) + 1;
            await db.outbox.update(item.id!, {
                tryCount,
                lastError: e?.message ?? String(e),
                createdAt: Date.now()
            });
        }
        return;
    }
}

export const syncEventsFromOnline = async (events: WorkEventOutput[]) => {
    for (const event of events) {
        const localEvent = await db.events.where({"idempotency_key": event.idempotency_key}).first()
        if (!localEvent) {
            try {
                //@ts-ignore
                await addEventOffline(event, event?.employee, LocalStatus.synced);
            } catch (e) {
                console.log(event)
            }
        } else if (localEvent.localStatus !== LocalStatus.synced) {
            db.events.update(localEvent.id!, {localStatus: LocalStatus.synced});
        }
    }
}

export enum SyncStatus {
    Init,
    Processing,
    Success,
    Failure,
}


interface SyncStore {
    syncStatus: SyncStatus
    _sync_content?: Map<string, any[]>
    syncEvents: (events?: WorkEventOutput[]) => void
    syncEmployee: (employee: Employee[]) => void
    syncEmployeesWorkDays: (workdays: WorkdayWithEmployee[]) => void
    retry: (target?: string) => void
}

export const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));


export enum Timeouts {
    Default = 5000,
    Error = 7000
}

export const useSyncStore = create<SyncStore>((set, getState) => ({
    syncStatus: SyncStatus.Init,
    syncEvents: async (events?: WorkEventOutput[]) => {
        const _sync_content = getState()?._sync_content || new Map<string, any[]>();
        _sync_content.set("events", events || [])
        set({syncStatus: SyncStatus.Processing, _sync_content: _sync_content});
        try {
            if (events) await syncEventsFromOnline(events)
            else await syncEventsOutbox()
        } catch (e) {
            set({syncStatus: SyncStatus.Failure});
            sleep(Timeouts.Error).then(() => set({syncStatus: SyncStatus.Init}))
            return
        }

        _sync_content.delete("events")
        sleep(Timeouts.Default).then(() => {
            set({syncStatus: SyncStatus.Success})
            sleep(Timeouts.Default).then(() => set({syncStatus: SyncStatus.Init}))
        })
    },
    retry: (target?: string) => {
        if (target === "events" || (!target && getState()?._sync_content?.get("events"))) {
            getState().syncEvents(getState()?._sync_content?.get("events") || [])
        }
        if (target === "employee" || (!target && getState()?._sync_content?.get("employee"))) {
            getState().syncEmployee(getState()?._sync_content?.get("employee") || [])
        }
    },
    syncEmployee: async (employee: Employee[]) => {
        if (employee?.length === 0) return

        const _sync_content = getState()?._sync_content || new Map<string, any[]>();
        _sync_content.set("employee", employee || [])
        set({syncStatus: SyncStatus.Init, _sync_content: _sync_content});
        try {
            await syncEmployee(employee)
        } catch (e) {
            set({syncStatus: SyncStatus.Failure});
            sleep(Timeouts.Error).then(() => set({syncStatus: SyncStatus.Init}))
            return
        }

        _sync_content.delete("employee")
        set({syncStatus: SyncStatus.Success, _sync_content});
        sleep(Timeouts.Default).then(() => set({syncStatus: SyncStatus.Init}))
    },
    syncEmployeesWorkDays: async (workdays: WorkdayWithEmployee[]) => {
        if (workdays?.length === 0) return
        try {
            await syncWorkdays(workdays)
        } catch (e) {
            set({syncStatus: SyncStatus.Failure});
            sleep(Timeouts.Error).then(() => set({syncStatus: SyncStatus.Init}))
            return
        }
    },
}))

